import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  Stepper, Step, StepLabel, StepButton, IconButton,
  Tabs, Tab, Divider,
  useTheme, useMediaQuery
} from '@mui/material';
// LƯỚI: dùng `ContainerGrid` (CSS grid + CONTAINER QUERY `@container responsive-grid`) chứ KHÔNG dùng
// Grid của MUI. Container query đo chiều rộng của CHÍNH KHUNG CHỨA, còn media query của MUI đo viewport —
// form này có thể được nhúng trong một khung hẹp (form con, ô tra cứu, bố cục chia đôi), khi đó lưới theo
// viewport sẽ nhét 4 field vào một hàng hẹp và field nào cũng teo lại.
import Grid from './ContainerGrid'; 

import CloseIcon from '@mui/icons-material/Close';
import AuthContext from "flexbiz-core/components/auth/Context";
import ManagedModal from './ManagedModal';

import BaseFieldControl from './BaseFieldControl';
import FileFieldControl from './FileFieldControl';
import ObjectArrayControl from './ObjectArrayControl';
import { splitVoucherFields, buildFormTabs, clampTabIndex, voucherFieldGrid, buildMasterGroups } from './voucherFormTabs';
import { useQueryClient } from '@tanstack/react-query';
import DynamicCustomForm from "./DynamicCustomForm";
import { resolveRequired, interpretCheckResult, coerceFieldValue } from './formRules';
import { buildValueChangedArgs } from './lifecycleHooks';
import * as utils from '../utils';
import Moment from 'moment';
import Numeral from 'numeral';
import RecordLookupMenu from './RecordLookupMenu';

const getNestedValue = (obj, path) => {
  if (!path || !obj) return undefined;
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined) ? acc[part] : undefined, obj);
};

const setNestedValue = (obj, path, value) => {
  if (!path) return obj;
  const parts = path.split('.');
  if (parts.length === 1) return { ...obj, [path]: value };
  const result = { ...obj };
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = { ...(current[part] || {}) };
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
  return result;
};
// =====================================================================
// COMPONENT FORM CHÍNH
// =====================================================================
export default function DataListForm({
  open, onClose, isEditing, initialData, fields, onSave, saving, checkBeforeSave,
  apiCode, changeCodeForm,   // cho MENU TRA CỨU trên tiêu đề form (chỉ hiện khi đang SỬA bản ghi đã lưu)
  formSize,
  // TỰ TÁCH NHÓM ở phần master ("Thông tin chung" / "Thông tin khác"). Bỏ trống = bật (tương thích ngược);
  // `false` = giữ NGUYÊN bố cục field như khai báo — cấu hình dịch từ listinfo luôn đặt false.
  autoGroupMaster,
  customFormScript,
  masterData // dữ liệu form mẹ khi DataListForm được mở như một form con (tuỳ chọn)
}) {
  const context = useContext(AuthContext);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);   // tab detail (bố cục chứng từ)
  const [isUploading, setIsUploading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  useEffect(() => {
    if (open) {
      const deepCloneInitial = JSON.parse(JSON.stringify(initialData || {}));
      setFormData(deepCloneInitial);
      setFormErrors({});
      setActiveStep(0); 
      setIsUploading(false);
      setIsDirty(false);
      
      const fileFields = fields.filter(f => f.type === 'File' || f.type === 'FileArray');
      fileFields.forEach(async (f) => {
        const val = deepCloneInitial?.[f.field];
        if (!val) return;
        
        const isArray = Array.isArray(val);
        // Chỉ hỏi bản ghi file với giá trị đúng dạng ObjectId. Giá trị cũ là ĐƯỜNG DẪN (vd rpt.file_mau_in =
        // '/templates/excels/a.xlsx') giữ nguyên chuỗi, FileFieldControl tự dựng link tải từ đường dẫn đó.
        const laObjectId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v);
        const idsToFetch = isArray ? val.filter(laObjectId) : (laObjectId(val) ? [val] : []);
        
        if (idsToFetch.length > 0) {
          try {
            const res = await context.apis.asyncGetList(userToken, "file", { condition: { _id: { $in: idsToFetch } }, limit: 100 });
            const fetchedFiles = res?.data || res || [];
            setFormData(prev => {
              const currentVal = prev[f.field];
              let newVal;
              if (Array.isArray(currentVal)) {
                newVal = currentVal.map(v => { if (typeof v === 'string') return fetchedFiles.find(file => file._id === v) || v; return v; });
              } else {
                newVal = fetchedFiles.find(file => file._id === currentVal) || currentVal;
              }
              return { ...prev, [f.field]: newVal };
            });
          } catch (e) { console.error(`Lỗi tải file:`, e); }
        }
      });
    }
  }, [open, initialData, fields, context, userToken, queryClient]); 

  const evaluateCondition = (condition) => {
    if (!condition) return false;
    if (typeof condition === 'function') {
      try {
        return condition(context, formData, masterData || formData, fields);
      } catch (error) {
        console.warn("Lỗi chạy script điều kiện (hideInForm/hideInList):", error);
        return false; // Nếu script lỗi, mặc định hiển thị ra cho an toàn
      }
    }
    return !!condition;
  };

  const visibleFieldsCount = fields.filter(f => !evaluateCondition(f.hideInForm)).length;
  let dialogMaxWidth = formSize || 'md'; 
  if (!formSize) { dialogMaxWidth = visibleFieldsCount <= 6 ? 'md' : 'lg'; }
  const isFullScreenDialog = dialogMaxWidth === 'full';

  const stepGroups = fields.reduce((acc, field) => {
    if (evaluateCondition(field.hideInForm)) return acc; 
    
    // ĐỔI 'field.step' THÀNH 'field.formStep' TẠI ĐÂY
    const stepNum = field.formStep || 1; 
    
    const stepLabel = field.stepLabel || `Bước ${stepNum}`; 
    if (!acc[stepNum]) acc[stepNum] = { label: stepLabel, fields: [] };
    acc[stepNum].fields.push(field);
    return acc;
  }, {});

  const stepKeys = Object.keys(stepGroups).sort((a, b) => Number(a) - Number(b));
  const totalSteps = stepKeys.length;
  const isMultiStep = totalSteps > 1; 
  const currentStepKey = stepKeys[activeStep];
  const currentFields = isMultiStep ? (stepGroups[currentStepKey]?.fields || []) : fields.filter(f => !evaluateCondition(f.hideInForm));

  // ===== BỐ CỤC "CHỨNG TỪ" (mặc định cho màn LỚN, KHÔNG step, KHÔNG custom script, CÓ bảng chi tiết) =====
  //  - Phần 1 (cố định trên): field master
  //  - Phần 2 (co giãn + scroll): các tab chi tiết (mỗi field ObjectArray = 1 tab)
  //  - Phần 3 (cố định dưới): field tổng cộng + nút Lưu/Thoát
  const visibleFormFields = fields.filter(f => !evaluateCondition(f.hideInForm));
  const isSummaryField = (f) => f.type !== 'ObjectArray' && (f.isSummary === true || (f.type === 'Number' && f.readOnly === true && /^t_/.test(f.field || '')));
  // Chia master / tab chi tiết / tab field / tổng cộng — luật ở voucherFormTabs.js.
  // Field khai `formTab` khác "Thông tin chung" thì rời khỏi master và thành tab riêng, xếp SAU các
  // tab chi tiết (đúng phương ngữ `tab` của InputBase).
  const {
    masterFields: masterFormFields, detailFields: detailArrayFields,
    summaryFields, extraTabs,
  } = splitVoucherFields(visibleFormFields, isSummaryField);
  const formTabs = buildFormTabs(detailArrayFields, extraTabs);
  // Chỉ có tab field mà không có lưới chi tiết cũng dùng bố cục chứng từ: nếu không thì các field đó
  // rơi trở lại một lưới dài, tức là khai tab xong chẳng thấy tab đâu.
  const useVoucherLayout = !isMobile && !customFormScript && !isMultiStep && formTabs.length > 0;
  // ===== CHIỀU CAO TỰ CO GIÃN =====
  // Mặc định form thường (không master-detail) sẽ CO theo nội dung: form 1 field thì thấp,
  // form nhiều field thì cao dần cho tới trần màn hình rồi mới scroll.
  // Giữ nguyên chiều cao TỐI ĐA cho:
  //  - Bố cục chứng từ (master-detail): bảng chi tiết cần đủ chỗ để hiển thị nhiều dòng.
  //  - Dialog fullScreen (formSize = 'full').
  //  - Form dùng customFormScript: script của user có thể tự canh layout theo 100% chiều cao.
  const useAutoHeight = !isMobile && !useVoucherLayout && !isFullScreenDialog && !customFormScript && !isMultiStep;
  const safeTab = clampTabIndex(activeTab, formTabs.length);

  // Chia MASTER thành khu vực — luật ở voucherFormTabs.buildMasterGroups.
  // `autoGroupMaster: false` (cấu hình DỊCH TỪ LISTINFO luôn đặt như vậy) thì KHÔNG tự tách
  // "Thông tin chung" / "Thông tin khác": listinfo đã có bố cục riêng của nó.
  const masterGroups = buildMasterGroups(masterFormFields, { autoGroup: autoGroupMaster });
  // Cỡ ô của field trên form chứng từ: `gridEdit` của field (kể cả dịch từ `grid_configs` của listinfo)
  // thắng, còn lại lấy mặc định theo khổ form — xem voucherFormTabs.voucherFieldGrid.
  // KHÔNG chia master thành 2 cột nữa: đặt lưới bên trong một Box rộng 50% làm `md:3` teo còn 1/8 form,
  // và mặc định cũ `{xs:12}` khiến mỗi field chiếm trọn một dòng -> master nuốt hết chiều cao.
  // Dùng `dialogMaxWidth` (khổ THẬT của dialog) chứ không phải `formSize` — formSize có thể bỏ trống
  // và khi đó khổ được suy ra theo số field (xem trên).
  const voucherGrid = (f) => voucherFieldGrid(f, dialogMaxWidth);

  // Cho phép nhảy tự do giữa các step khi đang ở chế độ Cập nhật (isEditing),
  // không cần validate/đi tuần tự qua từng step như khi Thêm mới.
  const allowFreeStepNavigation = isMultiStep && !customFormScript && isEditing;

  // 2. CẬP NHẬT HÀM handleFieldChange
  // `masterPatch` = thay đổi mà script của một DÒNG CHI TIẾT đã ghi vào bản ghi mẹ (`master.tien_hang =
  // …`). ObjectArrayControl chạy script trên một bản sao rồi gửi phần chênh lệch lên đây, vì sửa tại
  // chỗ vào state của form cha thì cha không vẽ lại. Gộp CÙNG một lần setState với mảng chi tiết mới —
  // hai lần setState rời nhau thì lần sau tính từ `formData` cũ và nuốt mất lần trước.
  const handleFieldChange = async (fieldConfig, newValue, optionObj, masterPatch) => {

    // Ép kiểu ngay tại CỬA VÀO, đúng chỗ bộ cũ làm (GroupTabs.js:1259). Ô số của react-number-format
    // trả về CHUỖI; để nguyên là script cộng `+` thành nối chuỗi và bản ghi lưu chuỗi ở field số.
    newValue = coerceFieldValue(fieldConfig, newValue);

    const oldValue = getNestedValue(formData, fieldConfig.field);

    let newFormData = setNestedValue(formData, fieldConfig.field, newValue);
    if (masterPatch && typeof masterPatch === 'object') newFormData = { ...newFormData, ...masterPatch };

    if ((fieldConfig.type === 'Autocomplete' || fieldConfig.type === 'Select') && optionObj) {
        const apiLabelMap = fieldConfig.apiMapping?.label || 'name';
        const formLabelMap = fieldConfig.apiMapping?.label_as || apiLabelMap;
        if (fieldConfig.field !== formLabelMap) {
            const labelValue = Array.isArray(optionObj) ? optionObj.map(o => o.label) : optionObj.label;
            newFormData = setNestedValue(newFormData, formLabelMap, labelValue);
        }
    }

    if (typeof fieldConfig.onValueChanged === 'function') {
      // Script của bộ cũ SỬA THẲNG vào `master`/`detail` chứ không `return` (`master.ty_gia =
      // ref_value.ty_gia||1;`). `newFormData` là object mới do setNestedValue tạo ra, nên truyền chính
      // nó vào là mọi phép gán cấp một của script rơi đúng chỗ; giá trị TRẢ VỀ (phương ngữ mới) được
      // trộn thêm lên trên. Script async được `await`.
      const before = { ...newFormData };
      try {
        let updates = fieldConfig.onValueChanged(...buildValueChangedArgs({
          field: fieldConfig.field, newValue, oldValue, optionObj,
          formData: newFormData, master: masterData || newFormData, oldData: formData,
          fields, context, utils, moment: Moment, numeral: Numeral,
        }));
        if (updates && typeof updates.then === 'function') updates = await updates;
        if (updates && typeof updates === 'object') newFormData = { ...newFormData, ...updates };
      } catch (e) {
        // Bộ cũ khôi phục lại TOÀN BỘ bản ghi, tức nuốt luôn thao tác vừa gõ của người dùng — gõ lại
        // thì lại hỏng tiếp. Ở đây chỉ lùi những gì SCRIPT đã ghi, giữ nguyên thay đổi của người dùng.
        newFormData = { ...newFormData, ...before };
        console.error(`[DataListForm] script onValueChanged của field "${fieldConfig.field}" lỗi:`, (e && e.message) || e);
      }
    }

    setFormData(newFormData);
    setIsDirty(true);
  };

  const validateFields = (fieldsToValidate) => {
    const errors = {};
    let isValid = true;
    
    fieldsToValidate.forEach(f => {
      const val = getNestedValue(formData, f.field);
      if (f.type === 'ObjectArray') {
        const arr = Array.isArray(val) ? val : [];
        if (resolveRequired(f, { context, formData, masterData: masterData || formData, fields }) && arr.length === 0) { errors[f.field] = `${f.label} yêu cầu ít nhất 1 dòng`; isValid = false; }
        if (f.subFields) {
          arr.forEach((item, index) => {
            f.subFields.forEach(subF => {
              // Dòng chi tiết: `formData` của script là chính DÒNG đó, `masterData` là bản ghi cha.
              if (resolveRequired(subF, { context, formData: item, masterData: formData, fields: f.subFields })
                  && (item[subF.field] === undefined || item[subF.field] === null || item[subF.field] === '')) {
                errors[`${f.field}_${index}_${subF.field}`] = `${subF.label} không được trống`; isValid = false;
              }
            });
          });
        }
        return;
      }

      if (resolveRequired(f, { context, formData, masterData: masterData || formData, fields })
          && (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0))) {
        errors[f.field] = `${f.label} là bắt buộc`; isValid = false; return;
      }
      
      if (f.type === 'Number' && val !== undefined && val !== '') {
        const num = Number(val);
        if (f.min !== undefined && num < f.min) { errors[f.field] = `Nhỏ nhất là ${f.min}`; isValid = false; }
        if (f.max !== undefined && num > f.max) { errors[f.field] = `Lớn nhất là ${f.max}`; isValid = false; }
      }
      
      if (f.type === 'String' && val && f.maxLength && String(val).length > f.maxLength) {
        errors[f.field] = `Tối đa ${f.maxLength} ký tự`; isValid = false;
      }
    });
    
    setFormErrors(prev => ({ ...prev, ...errors }));
    return isValid;
  };

  const handleNext = () => { if (validateFields(currentFields)) { setFormErrors({}); setActiveStep((prev) => prev + 1); }};
  const handleBack = () => { setFormErrors({}); setActiveStep((prev) => prev - 1); };

  // Nhảy trực tiếp tới 1 step bất kỳ (không validate step hiện tại) — chỉ dùng khi đang Cập nhật (isEditing)
  const handleStepClick = (stepIndex) => {
    if (!allowFreeStepNavigation || isLocked) return;
    setFormErrors({});
    setActiveStep(stepIndex);
  };

  const uploadSingleFile = (file) => {
    return new Promise((resolve, reject) => {
      let url = `${context.config.server_url}/api/${context.config.id_app}/file?json=1&access_token=${userToken}`;
      let data = { name: "file", is_avatar: file.is_avatar ? 1 : 0 }; 
      context.apis.uploadFile(url, file, (error, rs) => { if (error) reject(error); else resolve(JSON.parse(rs)); }, data);
    });
  };

  const handleSaveClick = async () => {
    // - Khi Thêm mới (isEditing = false) và đang ở form nhiều bước chuẩn (không custom script):
    //   chỉ validate các field của bước hiện tại (giữ hành vi cũ, đi tuần tự từng step).
    // - Khi Cập nhật (isEditing = true) hoặc các trường hợp còn lại: validate TOÀN BỘ field đang hiển thị,
    //   vì người dùng có thể lưu ngay tại bất kỳ step nào đã nhảy tới.
    const fieldsToValidate = (isMultiStep && !customFormScript && !isEditing)
      ? currentFields
      : fields.filter(f => !evaluateCondition(f.hideInForm));
    if (!validateFields(fieldsToValidate)) return;

    // KIỂM TRA TRƯỚC KHI LƯU ở cấp form (tương đương expression_check_save của listinfo): luật chéo giữa
    // phần master và lưới chi tiết mà validate từng field không diễn tả được. Trả về chuỗi/{error} là CHẶN.
    if (typeof checkBeforeSave === 'function') {
      try {
        const message = interpretCheckResult(await checkBeforeSave(context, formData, masterData || formData, fields));
        if (message) { context.alert(message, null, 'red'); return; }
      } catch (e) {
        context.alert(e?.message || e?.error || String(e), null, 'red');
        return;
      }
    }

    setIsUploading(true);
    let finalFormData = { ...formData };
    try {
      const fileFields = fields.filter(f => f.type === 'File' || f.type === 'FileArray');
      let oldFilesToUpdate = []; 

      for (let f of fileFields) {
        const fieldValue = finalFormData[f.field];
        if (!fieldValue) continue;
        
        if (f.type === 'FileArray') {
          let finalIds = [];
          for (let item of fieldValue) {
            if (item instanceof File) { 
               const uploadedData = await uploadSingleFile(item); 
               finalIds.push(uploadedData._id || uploadedData.id); 
            } else { 
               finalIds.push(item._id || item.id || item);
               if (typeof item === 'object') { oldFilesToUpdate.push({ _id: item._id || item.id, is_avatar: item.is_avatar ? 1 : 0 }); }
            }
          }
          finalFormData[f.field] = finalIds;
        } 
        else if (f.type === 'File') {
          if (fieldValue instanceof File) { 
             const uploadedData = await uploadSingleFile(fieldValue); 
             finalFormData[f.field] = uploadedData._id || uploadedData.id; 
          } else { 
             finalFormData[f.field] = fieldValue._id || fieldValue.id || fieldValue; 
             if (typeof fieldValue === 'object') { oldFilesToUpdate.push({ _id: fieldValue._id || fieldValue.id, is_avatar: fieldValue.is_avatar ? 1 : 0 }); }
          }
        }
      }

      if (oldFilesToUpdate.length > 0) {
          try { await context.apis.asyncPostList(userToken, 'file', oldFilesToUpdate); } 
          catch (fileUpdateErr) { console.warn("Lỗi cập nhật trạng thái Avatar cho file cũ:", fileUpdateErr); }
      }
      setIsDirty(false);
      // Gửi bản sạch lên cho Manager xử lý API
      //console.log("[onSave]",finalFormData);
      onSave(finalFormData, isEditing);
      
    } catch (error) {
      console.error("[DataListForm][handleSaveClick]",error);
      context.alert(error.message || "Quá trình tải file thất bại!", null, "red");
    } finally { 
      setIsUploading(false); 
    }
  };

  const handleCloseRequest = () => {
    if (isDirty) {
      context.alert(
        "Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn đóng form và hủy bỏ các thay đổi này?", 
        (yes) => { if (yes) { setIsDirty(false); onClose(); } }, 
        "warning"
      );
    } else { onClose(); }
  };

  // =====================================================================
  // HÀM HỖ TRỢ XUẤT CÁC CONTROL INPUT THUẦN (CHO SCRIPT CỦA USER)
  // =====================================================================
  const renderFieldControlOnly = (fieldName, extra) => {
    const f = fields.find(x => x.field === fieldName);
    if (!f) return <Typography color="error" variant="caption">[Chưa cấu hình trường: {fieldName}]</Typography>;

    const isReadOnly = evaluateCondition(f.readOnly);
    const err = formErrors[f.field];
    const val = getNestedValue(formData, f.field);

    if (f.type === 'File' || f.type === 'FileArray') {
      return <FileFieldControl field={f} value={val} onChange={(newVal) => handleFieldChange(f, newVal)} error={err} isReadOnly={isReadOnly} context={context} userToken={userToken} isUploading={isUploading} saving={saving}/>;
    } else if (f.type === 'ObjectArray') {
      return <ObjectArrayControl isMobile={isMobile} field={f} value={val} onChange={(newVal, masterPatch) => handleFieldChange(f, newVal, undefined, masterPatch)} formErrors={formErrors} isReadOnly={isReadOnly} masterData={formData} {...(extra || {})}/>;
    } else {
      return <BaseFieldControl field={f} value={val} onChange={(newVal, optionObj) => handleFieldChange(f, newVal, optionObj)} error={err} isReadOnly={isReadOnly} formData={formData} masterData={masterData || formData} formFields={fields}/>;
    }
  };

  const renderFieldDispatcher = (f) => {
    let defaultSize = { xs: 12 };
    if (f.type === 'Number' || f.type === 'Boolean' || f.type === 'Date' || f.type === 'DateTime') { defaultSize = { xs: 12, sm: 6 }; }
    const gridSize = f.gridEdit || defaultSize;
    return (
      <Grid size={gridSize} key={f.field}>
        {renderFieldControlOnly(f.field)}
      </Grid>
    );
  };

  const isLocked = saving || isUploading;
  const FormContent = (
    <Box sx={{ 
      display: 'flex', flexDirection: 'column', 
      // Mobile: giữ nguyên hành vi cũ (Drawer tự co theo nội dung, giới hạn bởi maxHeight).
      // Desktop: Paper của Dialog đã được gán "height" CỐ ĐỊNH (xem PaperProps phía dưới),
      // nên ở đây luôn lấp đầy 100% chiều cao đó -> footer luôn dính đáy, không phụ thuộc
      // vào lượng nội dung bên trong (giống hành vi trên mobile).
      maxHeight: isMobile ? '92dvh' : '100%',
      // Desktop tự co giãn: để 'auto' cho Box lấy đúng chiều cao nội dung (Paper cũng 'auto'),
      // vẫn bị chặn trên bởi maxHeight 100% của Paper -> vượt trần thì scroll.
      height: isMobile ? ((isMultiStep && !customFormScript) ? '92dvh' : '100%') : (useAutoHeight ? 'auto' : '100%'),
      ...(useAutoHeight ? { flex: '1 1 auto', minHeight: 0 } : {}),
      // BỐ CỤC CHỨNG TỪ: master / summary / footer CỐ ĐỊNH, chỉ vùng TAB co giãn.
      // `minHeight: 0` + `overflow: hidden` là hai thứ giữ hợp đồng đó: thiếu chúng, một phần master
      // quá dài sẽ nở ra đẩy thanh tiêu đề tab xuống dưới đáy khung -> không bấm chọn tab được nữa.
      ...(useVoucherLayout ? { minHeight: 0, overflow: 'hidden' } : {}),
      bgcolor: 'background.paper' 
    }}>
      
      <Box sx={{ flexShrink: 0, position: 'sticky', top: 0, zIndex: 2, bgcolor: '#f8f9fa', px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: 1.5, borderBottom: '1px solid #e0e0e0' }}>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Box sx={{ width: 40, height: 5, bgcolor: 'divider', borderRadius: 3 }} />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {isEditing ? 'Cập nhật dữ liệu' : 'Thêm dữ liệu mới'}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Tra cứu quanh chính bản ghi đang sửa — đúng chỗ bộ cũ đặt nút "..." trên MainForm. */}
            {isEditing && apiCode && initialData && (initialData._id || initialData.id) && (
              <RecordLookupMenu item={initialData} apiCode={apiCode} changeCodeForm={changeCodeForm} />
            )}
            <IconButton onClick={handleCloseRequest} disabled={isLocked} size="small" sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', '&:hover': { bgcolor: '#f0f0f0' } }}>
               <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Ẩn Stepper nếu user đang xài Custom Script để tránh bể layout của họ */}
        {isMultiStep && !customFormScript && (
          <Box sx={{ width: '100%', mt: 2 }}>
            {/* Khi đang Cập nhật (isEditing): Stepper cho phép bấm thẳng vào bất kỳ step nào (nonLinear).
                Khi Thêm mới: giữ nguyên Stepper chỉ hiển thị tiến trình, không cho bấm tắt. */}
            <Stepper activeStep={activeStep} alternativeLabel nonLinear={allowFreeStepNavigation}>
              {stepKeys.map((key, idx) => (
                <Step key={key}>
                  {allowFreeStepNavigation ? (
                    <StepButton onClick={() => handleStepClick(idx)} disabled={isLocked}>
                      {stepGroups[key].label}
                    </StepButton>
                  ) : (
                    <StepLabel>{stepGroups[key].label}</StepLabel>
                  )}
                </Step>
              ))}
            </Stepper>
          </Box>
        )}
      </Box>

      {/* ================= BỐ CỤC CHỨNG TỪ (màn lớn, không step): master | tab chi tiết | tổng + nút ================= */}
      {useVoucherLayout && (
        <>
          {/* Phần 1: MASTER — cố định trên; 2 cột (tự chia) / nhiều section (formGroup) / 1 lưới (tự sắp) */}
          {/* Phần 1: MASTER — CỐ ĐỊNH, có trần và tự cuộn.
              Trần dùng `min(46%, 42dvh)`: phần trăm chỉ giải được khi khung cha có chiều cao xác định,
              còn `dvh` thì luôn giải được — nhờ vậy master không bao giờ nở quá nửa form dù khung cha
              có rơi vào trạng thái chiều cao 'auto'. */}
          <Box sx={{ flex: '0 1 auto', minHeight: 0, bgcolor: '#ffffff', px: { xs: 2, sm: 3 }, py: 2, overflowY: 'auto', maxHeight: 'min(46%, 42dvh)' }}>
            {/* MỘT lưới TRÀN chiều rộng cho mỗi khu vực. Mỗi field chiếm cỡ do `gridEdit`/mặc định quyết
                định, nên form khổ full xếp được 4 field một hàng thay vì 1. */}
            {masterGroups ? masterGroups.map((grp, gi) => (
              <Box key={gi} sx={{ mb: gi < masterGroups.length - 1 ? 1.5 : 0 }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>{grp.title}</Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={2} alignItems="flex-start">
                  {grp.fields.map(f => <Grid size={voucherGrid(f)} key={f.field}>{renderFieldControlOnly(f.field)}</Grid>)}
                </Grid>
              </Box>
            )) : (
              <Grid container spacing={2} alignItems="flex-start">
                {masterFormFields.map(f => <Grid size={voucherGrid(f)} key={f.field}>{renderFieldControlOnly(f.field)}</Grid>)}
              </Grid>
            )}
          </Box>
          {/* Phần 2: TAB — lưới chi tiết trước, tab field sau; co giãn + scroll */}
          <Box sx={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff', px: { xs: 2, sm: 3 }, pt: 0 }}>
            {formTabs.length > 1 && (
              <Tabs value={safeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ flexShrink: 0, borderBottom: '1px solid #e0e0e0', minHeight: 42 }}>
                {formTabs.map((t, i) => <Tab key={`${t.kind}_${t.field ? t.field.field : t.title}_${i}`} label={t.title} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 42 }} />)}
              </Tabs>
            )}
            <Box sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', py: 1.5, ...(formTabs[safeTab]?.kind === 'fields' ? { overflowY: 'auto' } : {}) }}>
              {formTabs[safeTab]?.kind === 'detail'
                ? renderFieldControlOnly(formTabs[safeTab].field.field, { fillHeight: true })
                : formTabs[safeTab]?.kind === 'fields'
                  ? <Grid container spacing={2} alignItems="flex-start">{formTabs[safeTab].fields.map(f => <Grid size={voucherGrid(f)} key={f.field}>{renderFieldControlOnly(f.field)}</Grid>)}</Grid>
                  : null}
            </Box>
          </Box>
          {/* Phần 3: 2 CỘT — cột 1 field tổng cộng, cột 2 nút (footer gọn 1 dòng khi ít field tổng) */}
          <Box sx={{ flex: '0 0 auto', bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0', px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {summaryFields.length > 0 && (
                <Grid container spacing={2}>
                  {summaryFields.map(f => <Grid size={f.gridEdit || { xs: 6, sm: 'auto' }} key={f.field} sx={{ minWidth: 150 }}>{renderFieldControlOnly(f.field)}</Grid>)}
                </Grid>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button variant="outlined" size="large" onClick={handleCloseRequest} disabled={isLocked} sx={{ borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', bgcolor: 'white', borderColor: '#d1d1d6', color: 'text.primary', minWidth: 120 }}>Thoát</Button>
              <Button variant="contained" size="large" onClick={handleSaveClick} disabled={isLocked} startIcon={isLocked && <CircularProgress size={20} color="inherit" />} sx={{ bgcolor: 'primary.main', borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', boxShadow: 'none', minWidth: 160 }}>
                {isUploading ? 'Đang tải...' : (isEditing ? 'Lưu thay đổi' : 'Tạo phiếu')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      {!useVoucherLayout && (
      <>
      {/* ================= KHU VỰC RENDER GIAO DIỆN (ĐIỀU HƯỚNG) ================= */}
      {/* minHeight: 0 để flex item co lại được -> khi nội dung vượt trần thì vùng này scroll,
          còn khi nội dung ngắn thì nó chỉ cao bằng nội dung (form không bị kéo giãn vô ích). */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', bgcolor: '#ffffff', px: { xs: 2, sm: 3 }, py: 3 }}>
        {customFormScript ? (
          // RENDER BẰNG CODE CỦA USER
          <DynamicCustomForm 
            script={customFormScript} 
            formData={formData} 
            setFormData={setFormData} 
            handleFieldChange={handleFieldChange}
            handleSaveClick={handleSaveClick}
            handleCloseRequest={handleCloseRequest}
            formErrors={formErrors} 
            renderField={renderFieldControlOnly} // Bơm hàm xuất Control thuần cho Script
            context={context} 
          />
        ) : (
          // RENDER CHUẨN CỦA HỆ THỐNG
          <Grid container spacing={2}>
            {currentFields.map(f => renderFieldDispatcher(f))}
          </Grid>
        )}
      </Box>

      <Box sx={{ flexShrink: 0, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0', px: { xs: 2, sm: 3 }, py: 2, pb: isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 2, display: 'flex', gap: 1.5, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
        {(isMultiStep && !customFormScript) ? (
          allowFreeStepNavigation ? (
            <>
              {/* Chế độ Cập nhật + nhiều step: có thể lưu ngay tại step đang xem,
                  kèm 2 nút điều hướng nhanh (không bắt buộc, không validate) để đi tới step kế/trước. */}
              <Button variant="outlined" size="large" onClick={() => { setFormErrors({}); setActiveStep((prev) => prev - 1); }}
                disabled={activeStep === 0 || isLocked} sx={{ borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', bgcolor: 'white', borderColor: '#d1d1d6', color: 'text.primary', display: activeStep === 0 ? 'none' : 'flex', minWidth: { sm: 120 } }}>
                Quay lại
              </Button>

              <Box sx={{ flexGrow: isMobile ? 0 : 1 }} />

              {activeStep < totalSteps - 1 && (
                <Button variant="outlined" size="large" onClick={() => { setFormErrors({}); setActiveStep((prev) => prev + 1); }}
                  disabled={isLocked} sx={{ borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', minWidth: { sm: 120 } }}>
                  Bước tiếp theo
                </Button>
              )}

              <Button variant="contained" size="large" onClick={handleSaveClick} disabled={isLocked} startIcon={isLocked && <CircularProgress size={20} color="inherit" />}
                sx={{ bgcolor: 'success.main', borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', boxShadow: 'none', minWidth: { sm: 150 } }}>
                {isUploading ? 'Đang tải...' : 'Lưu thay đổi'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="large" onClick={handleBack} disabled={activeStep === 0 || isLocked} fullWidth={isMobile} sx={{ borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', bgcolor: 'white', borderColor: '#d1d1d6', color: 'text.primary', display: activeStep === 0 ? 'none' : 'flex', minWidth: { sm: 120 } }}>
                Quay lại
              </Button>
              {activeStep < totalSteps - 1 ? (
                <Button variant="contained" size="large" onClick={handleNext} fullWidth={isMobile} sx={{ bgcolor: 'primary.main', borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', boxShadow: 'none', minWidth: { sm: 120 } }}>
                  Tiếp theo
                </Button>
              ) : (
                <Button variant="contained" size="large" onClick={handleSaveClick} disabled={isLocked} fullWidth={isMobile} startIcon={isLocked && <CircularProgress size={20} color="inherit" />} sx={{ bgcolor: 'success.main', borderRadius: '12px', fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', boxShadow: 'none', minWidth: { sm: 150 } }}>
                  {isUploading ? 'Đang tải...' : 'Hoàn tất & Lưu'}
                </Button>
              )}
            </>
          )
        ) : (
          <Button variant="contained" size="large" onClick={handleSaveClick} disabled={isLocked} fullWidth={isMobile} startIcon={isLocked && <CircularProgress size={20} color="inherit" />} sx={{ bgcolor: 'primary.main', borderRadius: '12px', py: { xs: 1.2, sm: 1 }, fontWeight: 'bold', textTransform: 'none', fontSize: '1rem', boxShadow: 'none', minWidth: { sm: 160 } }}>
            {isUploading ? 'Đang tải file lên...' : (isEditing ? 'Lưu thay đổi' : 'Tạo phiếu')}
          </Button>
        )}
      </Box>
      </>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <ManagedModal type="drawer" anchor="bottom" open={open} onClose={!isLocked ? handleCloseRequest : null} 
        PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 0, overflow: 'hidden' } }}
      >
        {FormContent}
      </ManagedModal>
    );
  }

  return (
    <ManagedModal type="dialog" open={open} onClose={!isLocked ? handleCloseRequest : null} maxWidth={isFullScreenDialog ? false : dialogMaxWidth} fullWidth fullScreen={isFullScreenDialog} 
      PaperProps={{ 
        sx: { 
          borderRadius: isFullScreenDialog ? 0 : 3, 
          padding: 0, 
          overflow: 'hidden',
          // - Form thường (useAutoHeight): height 'auto' -> Paper co theo nội dung form,
          //   maxHeight bên dưới là trần; vượt trần thì vùng nội dung tự scroll.
          // - Master-detail / fullScreen / custom script: giữ "height" CỐ ĐỊNH như trước để Paper
          //   luôn chiếm đủ chiều cao, thanh nút dính đáy giống mobile.
          height: isFullScreenDialog ? '100dvh' : (useAutoHeight ? 'auto' : 'calc(100dvh - 64px)'),
          maxHeight: isFullScreenDialog ? '100dvh' : 'calc(100dvh - 64px)', // Giữ toàn bộ modal nằm trong màn hình
          display: 'flex',
          flexDirection: 'column'
        } 
      }}
    >
      {FormContent}
    </ManagedModal>
  );
}